<template>
  <div>
    <div class="list-toolbar">
      <div class="toolbar-top">
        <div>
          <h2 class="list-title">Crew History</h2>
          <p class="list-subtitle">
            View all crew clock events for any date range
          </p>
        </div>
        <div class="toolbar-actions">
          <b-button variant="secondary" size="sm" @click="reload">
            <b-icon-arrow-clockwise /> Refresh
          </b-button>
          <b-button
            variant="primary"
            size="sm"
            :disabled="!events.length"
            @click="openExportModal"
          >
            <b-icon-download /> Export
          </b-button>
        </div>
      </div>

      <div class="toolbar-row">
        <div class="filter-field filter-field-date">
          <label class="filter-label">From</label>
          <b-form-datepicker
            v-model="dateFrom"
            locale="en-AU"
            :date-format-options="dateFormatOptions"
            size="sm"
            today-button
            reset-button
            close-button
            show-decade-nav
            class="date-input"
            @input="onDateFromChange"
          />
        </div>

        <div class="filter-field filter-field-date">
          <label class="filter-label">To</label>
          <b-form-datepicker
            v-model="dateTo"
            locale="en-AU"
            :date-format-options="dateFormatOptions"
            size="sm"
            today-button
            reset-button
            close-button
            show-decade-nav
            class="date-input"
            @input="reload"
          />
        </div>

        <div class="filter-field">
          <label class="filter-label">Project</label>
          <b-form-select
            v-model="selectedProjectId"
            :options="projectOptions"
            size="sm"
            class="filter-control filter-control-project"
            @change="reload"
          />
        </div>

        <div class="filter-field">
          <label class="filter-label">Status</label>
          <b-form-select
            v-model="statusFilter"
            :options="statusOptions"
            size="sm"
            class="filter-control filter-control-status"
            @change="reload"
          />
        </div>

        <div class="filter-field">
          <label class="filter-label">Week</label>
          <div class="week-nav">
            <b-button
              variant="secondary"
              size="sm"
              class="week-nav-btn"
              aria-label="Current week"
              title="Current week"
              @click="applyCurrentWeek"
            >
              <b-icon-calendar3 />
            </b-button>
            <b-button
              variant="outline-secondary"
              size="sm"
              class="week-nav-btn"
              aria-label="Previous week"
              @click="shiftWeek(-1)"
            >
              <b-icon-chevron-left />
            </b-button>
            <span class="week-nav-label">{{ weekLabel }}</span>
            <b-button
              variant="outline-secondary"
              size="sm"
              class="week-nav-btn"
              aria-label="Next week"
              @click="shiftWeek(1)"
            >
              <b-icon-chevron-right />
            </b-button>
          </div>
        </div>

        <div class="filter-field">
          <label class="filter-label">Search</label>
          <b-form-input
            v-model="searchQuery"
            placeholder="Tradie name…"
            size="sm"
            class="filter-control filter-control-search"
          />
        </div>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" />
    </div>

    <div
      v-else-if="filteredEvents.length === 0"
      class="text-center py-5 text-muted"
    >
      <p v-if="events.length === 0">No crew events for this range.</p>
      <p v-else>No events match your filters.</p>
    </div>

    <div v-else class="table-wrap">
      <!-- stacked="lg": below 992px each entry renders as a compact
           label/value card — never a horizontally scrolling table.
           From 992px up the full table fits without overflow. -->
      <b-table
        :items="filteredEvents"
        :fields="fields"
        hover
        stacked="lg"
        class="events-table mb-0"
      >
        <template #cell(worker)="{ item }">{{
          (item.tradie && item.tradie.name) || '-'
        }}</template>
        <template #cell(project)="{ item }">{{
          projectNameFromEvent(item, projects)
        }}</template>
        <template #cell(zone)="{ item }">{{
          zoneDisplayName(item.zone)
        }}</template>
        <template #cell(date)="{ item }">{{
          formatBusinessDateTime(
            item.clocked_in_at,
            'D MMM YYYY',
            item.timezone,
          )
        }}</template>
        <template #cell(times)="{ item }">
          {{
            formatBusinessDateTime(item.clocked_in_at, undefined, item.timezone)
          }}
          →
          {{
            formatBusinessDateTime(
              item.clocked_out_at,
              undefined,
              item.timezone,
            )
          }}
          <span
            v-if="timezoneLabel(item.timezone)"
            class="text-muted small ml-1"
            >{{ timezoneLabel(item.timezone) }}</span
          >
        </template>
        <template #cell(hours)="{ item }">{{
          item.clocked_in_at && item.clocked_out_at
            ? calcHours(item) + 'h'
            : '-'
        }}</template>
        <template #cell(status)="{ item }">
          <span class="status-pill" :class="eventStatusClass(item)">{{
            eventStatusLabel(item)
          }}</span>
        </template>
        <template #cell(actions)="{ item }">
          <div class="action-group">
            <b-button
              size="sm"
              variant="outline-secondary"
              @click="$refs.detailModal.open(item)"
              >View</b-button
            >
            <b-button-group v-if="isApprovableEvent(item)" size="sm">
              <b-button variant="outline-success" @click="approve(item.id)"
                >Approve</b-button
              >
              <b-button
                variant="outline-danger"
                @click="$refs.detailModal.open(item)"
                >Reject</b-button
              >
            </b-button-group>
          </div>
        </template>
      </b-table>
    </div>

    <timesheet-entry-detail-modal
      ref="detailModal"
      :can-approve="true"
      :projects="projects"
      @approve="approve"
      @reject="reject"
    />

    <supervisor-export-modal
      :export-tradie-search.sync="exportTradieSearch"
      :worker-filter.sync="workerFilter"
      :export-format.sync="exportFormat"
      show-statuses
      :statuses.sync="exportStatuses"
      show-dates
      :date-from.sync="dateFrom"
      :date-to.sync="dateTo"
      show-leave-types
      :leave-types.sync="exportLeaveTypes"
      show-columns
      :columns.sync="exportColumns"
      show-sort
      :sort-by.sync="exportSortBy"
      :filtered-worker-options="filteredWorkerOptions"
      :all-filtered-tradies-selected="allFilteredTradiesSelected"
      @toggle-all-tradies="toggleAllTradies"
      @range-change="reload"
      @export="exportReport"
    />
  </div>
</template>

<script>
import dayjs from 'dayjs'
import Api from '@/api'
import {
  zoneDisplayName,
  projectNameFromEvent,
  eventStatusLabel,
  eventStatusClass,
  formatBusinessDateTime,
  timezoneLabel,
  eventMinutes,
  isApprovableEvent,
  buildTimesheetExportRows,
  leaveTypeLabel,
} from '@/utils/timesheetHelpers'
import { startDayOf, weekDates } from '@/utils/weekHelpers'
import { downloadRowsAsPdf, downloadRowsAsCsv } from '@/utils/pdfExport'
import TimesheetEntryDetailModal from '@/components/timesheets/TimesheetEntryDetailModal.vue'
import SupervisorExportModal from '@/components/timesheets/supervisor/SupervisorExportModal.vue'

export default {
  name: 'CrewHistoryTab',
  components: { TimesheetEntryDetailModal, SupervisorExportModal },

  data() {
    return {
      events: [],
      loading: false,
      dateFrom: '',
      dateTo: '',
      // en-AU default renders "29 August 2026"; force numeric dd/mm/yyyy.
      dateFormatOptions: { year: 'numeric', month: '2-digit', day: '2-digit' },
      selectedProjectId: null,
      statusFilter: null,
      searchQuery: '',
      // Export modal (mirrors the BA "Generate Timesheet Report" tradie
      // picker — SupervisorExportModal, shared with Supervisor Timesheets).
      exportTradieSearch: '',
      workerFilter: [],
      exportFormat: 'csv',
      // Clock statuses to include in the export (all by default); empty
      // exports nothing, same as the BA modal.
      exportStatuses: ['clocked_in', 'clocked_out', 'approved', 'rejected'],
      exportLeaveTypes: [
        'public_holiday',
        'personal_leave',
        'sick_leave',
        'annual_leave',
      ],
      // Column toggles, keyed to buildTimesheetExportRows. Defaults match the
      // BA report (Break + Work Comments off).
      exportColumns: {
        date: true,
        tradieName: true,
        project: true,
        zone: true,
        startTime: true,
        endTime: true,
        hours: true,
        breakMinutes: false,
        status: true,
        comment: false,
        shift: true,
      },
      exportSortBy: 'date',
      statusOptions: [
        { value: null, text: 'All Statuses' },
        { value: 'clocked_in', text: 'Clocked In' },
        { value: 'clocked_out', text: 'Clocked Out' },
        { value: 'approved', text: 'Approved' },
        { value: 'rejected', text: 'Rejected' },
      ],
    }
  },

  computed: {
    projects() {
      return this.$store.state.project.projects || []
    },

    projectOptions() {
      return this.$store.getters['timesheet/projectOptions']
    },

    weekLabel() {
      if (!this.dateFrom || !this.dateTo) return '-'
      const from = new Date(this.dateFrom + 'T00:00:00')
      const to = new Date(this.dateTo + 'T00:00:00')
      const month = (d) => d.toLocaleDateString('en-AU', { month: 'short' })
      // Non-breaking space inside each date so the label can only ever wrap
      // around the dash, never mid-date.
      const nbsp = ' '
      if (
        from.getMonth() === to.getMonth() &&
        from.getFullYear() === to.getFullYear()
      ) {
        // Same month: compact "13–19 Jul" fits phone-width filter columns.
        return `${from.getDate()}–${to.getDate()}${nbsp}${month(to)}`
      }
      return `${from.getDate()}${nbsp}${month(
        from,
      )} – ${to.getDate()}${nbsp}${month(to)}`
    },

    filteredEvents() {
      const q = this.searchQuery.trim().toLowerCase()
      const status = this.statusFilter
      return this.events.filter((e) => {
        // Client-side status fallback: the `status` param is also sent to the
        // API, but filter here too so the dropdown works regardless of whether
        // the index endpoint honours it.
        if (status && e.status !== status) return false
        if (q) {
          const name = (e.tradie && e.tradie.name) || ''
          if (!name.toLowerCase().includes(q)) return false
        }
        return true
      })
    },

    // Every tradie present in the loaded events — the export picker list is
    // independent of the on-page status/search filters (same as the BA modal).
    workerOptions() {
      const seen = {}
      this.events.forEach((e) => {
        if (e.tradie && e.tradie.id != null) {
          seen[e.tradie.id] = e.tradie.name || `Tradie #${e.tradie.id}`
        }
      })
      return Object.keys(seen)
        .map((id) => ({ value: Number(id), text: seen[id] }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },

    filteredWorkerOptions() {
      const q = this.exportTradieSearch.trim().toLowerCase()
      if (!q) return this.workerOptions
      return this.workerOptions.filter((opt) =>
        opt.text.toLowerCase().includes(q),
      )
    },

    allFilteredTradiesSelected() {
      return (
        this.filteredWorkerOptions.length > 0 &&
        this.filteredWorkerOptions.every((opt) =>
          this.workerFilter.includes(opt.value),
        )
      )
    },

    fields() {
      return [
        { key: 'worker', label: 'Worker' },
        { key: 'project', label: 'Project' },
        { key: 'zone', label: 'Zone' },
        { key: 'date', label: 'Date' },
        { key: 'times', label: 'Times' },
        { key: 'hours', label: 'Hours', thStyle: 'width: 70px' },
        { key: 'status', label: 'Status', thStyle: 'width: 120px' },
        {
          key: 'actions',
          label: '',
          thStyle: 'width: 200px',
          tdClass: 'actions-cell',
        },
      ]
    },
  },

  watch: {
    // Default the project filter to the first available project once options
    // load (scoped users have no "All Projects" entry, so null renders blank).
    projectOptions: {
      immediate: true,
      handler(options) {
        if (this.selectedProjectId != null) return
        if (options.some((o) => o.value == null && !o.disabled)) return
        const first = options.find((o) => o.value != null && !o.disabled)
        if (!first) return
        this.selectedProjectId = first.value
        // Only reload if the week range is set; otherwise mounted's
        // applyCurrentWeek() → reload() picks up this selection.
        if (this.dateFrom && this.dateTo) this.reload()
      },
    },
  },

  async mounted() {
    if (!this.projects.length) {
      this.$store
        .dispatch('project/getProjects', { limit: 100, is_archived: false })
        .catch(function () {})
    }
    // Cached after first load — cheap. Must resolve before applyCurrentWeek()
    // so the default week honours the business's configured start day.
    await this.$store.dispatch('timesheet/getTimesheetSettings')
    // Per-project pay-period overrides so applyCurrentWeek() can honour a
    // project on a different week. Cached; failure falls back to company.
    // Must also resolve before applyCurrentWeek() — otherwise the first
    // computed range silently uses the company default even for a project
    // with its own override, since the override list hasn't loaded yet.
    await this.$store.dispatch('timesheet/getProjectPayPeriods')
    // applyCurrentWeek() sets the default From/To range and triggers the
    // initial load() via reload(); no separate load() call is needed.
    this.applyCurrentWeek()
  },

  methods: {
    zoneDisplayName,
    projectNameFromEvent,
    eventStatusLabel,
    eventStatusClass,
    isApprovableEvent,
    formatBusinessDateTime,
    timezoneLabel,

    applyCurrentWeek() {
      const startDay = this.$store.getters['timesheet/projectStartDay'](
        this.selectedProjectId,
      )
      const weekStart = startDayOf(null, startDay)
      this.dateFrom = weekStart
      this.dateTo = weekDates(weekStart)[6]
      this.reload()
    },

    onDateFromChange() {
      if (!this.dateFrom) return
      const from = new Date(this.dateFrom + 'T00:00:00')
      const to = new Date(from)
      to.setDate(from.getDate() + 6)
      this.dateTo = to.toISOString().slice(0, 10)
      this.reload()
    },

    shiftWeek(direction) {
      const from = new Date((this.dateFrom || this.dateTo) + 'T00:00:00')
      const to = new Date((this.dateTo || this.dateFrom) + 'T00:00:00')
      from.setDate(from.getDate() + direction * 7)
      to.setDate(to.getDate() + direction * 7)
      const fmt = (d) => d.toISOString().slice(0, 10)
      this.dateFrom = fmt(from)
      this.dateTo = fmt(to)
      this.reload()
    },

    calcHours(event) {
      const mins = eventMinutes(
        event.clocked_in_at,
        event.clocked_out_at,
        event.break_minutes,
      )
      return (mins / 60).toFixed(1)
    },

    reload() {
      this.load()
    },

    openExportModal() {
      this.exportTradieSearch = ''
      this.$bvModal.show('supervisor-export-modal')
    },

    toggleAllTradies() {
      // Operates on the tradies currently shown (respects the search filter).
      const shown = this.filteredWorkerOptions.map((opt) => opt.value)
      if (this.allFilteredTradiesSelected) {
        this.workerFilter = this.workerFilter.filter(
          (id) => !shown.includes(id),
        )
      } else {
        this.workerFilter = [...new Set([...this.workerFilter, ...shown])]
      }
    },

    // One synthetic row per leave day, shaped for buildTimesheetExportRows
    // (is_leave branch). Mirrors TimesheetListTab.syntheticLeaveEvent.
    syntheticLeaveEvent(entry, dayIso) {
      return {
        id: 'leave-' + entry.id + '-' + dayIso,
        is_leave: true,
        leave_type: entry.leave_type,
        leave_note: entry.note,
        leave_hours_per_day: entry.hours_per_day,
        leave_resolved_hours: entry.resolved_hours,
        leave_date: dayIso,
        tradie: entry.worker
          ? {
              id: entry.worker.id,
              name: entry.worker.full_name,
              trade: entry.worker.trade,
            }
          : null,
        project: {
          id: entry.project_id || null,
          name: entry.project_id
            ? projectNameFromEvent(
                { project_id: entry.project_id },
                this.projects,
              )
            : '',
        },
      }
    },

    async exportReport() {
      // Clocked events: page filters (filteredEvents) + the modal's status
      // checkboxes + tradie picker. Empty status list / unmatched picker
      // exports nothing, same as the BA modal.
      const scoped = this.filteredEvents.filter((e) => {
        if (!this.exportStatuses.includes(e.status)) return false
        if (
          this.workerFilter.length &&
          !(e.tradie && this.workerFilter.includes(e.tradie.id))
        ) {
          return false
        }
        return true
      })

      // Leave rows for the same window. If /leave-entries can't be loaded
      // (e.g. an older API build where it's still BA-only) fall back to a
      // clock-only export, but tell the user rather than silently dropping
      // the leave they asked for.
      let leaveRows = []
      let leaveFetchFailed = false
      if (this.exportLeaveTypes.length && this.dateFrom && this.dateTo) {
        try {
          const params = { date_from: this.dateFrom, date_to: this.dateTo }
          if (this.selectedProjectId) params.project_id = this.selectedProjectId
          const res = await Api.get('/leave-entries', { params })
          const entries = (res.data && res.data.data) || []
          entries.forEach((entry) => {
            if (!this.exportLeaveTypes.includes(entry.leave_type)) return
            let cursor = dayjs(entry.start_date).isAfter(dayjs(this.dateFrom))
              ? dayjs(entry.start_date)
              : dayjs(this.dateFrom)
            const end = dayjs(entry.end_date).isBefore(dayjs(this.dateTo))
              ? dayjs(entry.end_date)
              : dayjs(this.dateTo)
            let guard = 0
            while (
              cursor.format('YYYY-MM-DD') <= end.format('YYYY-MM-DD') &&
              guard < 400
            ) {
              const dayIso = cursor.format('YYYY-MM-DD')
              const weekend = cursor.day() === 0 || cursor.day() === 6
              if (entry.include_weekends || !weekend) {
                leaveRows.push(this.syntheticLeaveEvent(entry, dayIso))
              }
              cursor = cursor.add(1, 'day')
              guard += 1
            }
          })
          if (this.workerFilter.length) {
            leaveRows = leaveRows.filter(
              (e) => e.tradie && this.workerFilter.includes(e.tradie.id),
            )
          }
        } catch (e) {
          leaveRows = []
          leaveFetchFailed = true
        }
      }

      let combined = scoped.concat(leaveRows)
      if (!combined.length) {
        this.$bvToast.toast('No events to export', {
          variant: 'warning',
          title: 'Export',
        })
        return
      }

      if (leaveFetchFailed) {
        this.$bvToast.toast(
          "Couldn't load leave for this range — exported timesheet events only.",
          { variant: 'warning', title: 'Export' },
        )
      }

      const statusSortKey = (e) =>
        e.is_leave ? leaveTypeLabel(e.leave_type) || '' : e.status || ''
      const dateSortKey = (e) => (e.is_leave ? e.leave_date : e.clocked_in_at)
      const sortFns = {
        date: (a, b) => new Date(dateSortKey(b)) - new Date(dateSortKey(a)),
        date_asc: (a, b) => new Date(dateSortKey(a)) - new Date(dateSortKey(b)),
        tradie: (a, b) =>
          ((a.tradie && a.tradie.name) || '').localeCompare(
            (b.tradie && b.tradie.name) || '',
          ),
        project: (a, b) =>
          ((a.project && a.project.name) || '').localeCompare(
            (b.project && b.project.name) || '',
          ),
        status: (a, b) => statusSortKey(a).localeCompare(statusSortKey(b)),
      }
      combined = combined
        .slice()
        .sort(sortFns[this.exportSortBy] || sortFns.date)

      const rows = buildTimesheetExportRows(combined, this.exportColumns)

      const today = new Date().toLocaleDateString('en-AU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
      const filename = `Crew History ${today.replace(/\//g, ' ')}`

      if (this.exportFormat === 'pdf') {
        downloadRowsAsPdf(filename, rows)
      } else {
        downloadRowsAsCsv(filename, rows)
      }
    },

    async load() {
      this.loading = true
      try {
        var allEvents = []
        var page = 1
        var lastPage = 1
        do {
          var params = {
            date_from: this.dateFrom,
            date_to: this.dateTo,
            per_page: 100,
            page: page,
          }
          if (this.selectedProjectId) params.project_id = this.selectedProjectId
          if (this.statusFilter) params.status = this.statusFilter
          var res = await Api.get('/timesheet-events', { params })
          allEvents = allEvents.concat(res.data.events || [])
          lastPage =
            (res.data.meta && res.data.meta.last_page) ||
            res.data.last_page ||
            1
          page++
        } while (page <= lastPage)
        this.events = allEvents
      } catch (e) {
        this.$bvToast.toast('Failed to load crew history.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.loading = false
      }
    },

    // A pending_confirmation entry (a pre-entered "scheduled" shift whose
    // date has passed) must have its confirmation cleared before the API
    // will approve or reject it — see eventStatusLabel() in
    // timesheetHelpers.js and docs/designs/future-date-timesheet-entries.md.
    // Called before the approve/reject dispatch so it stays one click; a
    // failure here aborts the decision (caught by the caller's toast).
    async confirmIfPending(eventId) {
      var item = this.events.find(function (e) {
        return e.id === eventId
      })
      if (item && item.confirmation_status === 'pending_confirmation') {
        await this.$store.dispatch('timesheet/confirmEvent', eventId)
      }
    },

    async approve(eventId) {
      try {
        await this.confirmIfPending(eventId)
        await this.$store.dispatch('timesheet/approveEvent', {
          eventId,
          decision: 'approved',
        })
        this.$bvToast.toast('Timesheet approved.', {
          title: 'Success',
          variant: 'success',
          solid: true,
        })
        this.load()
      } catch (e) {
        this.$bvToast.toast('Failed to approve timesheet.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },

    async reject(payload) {
      try {
        await this.confirmIfPending(payload.eventId)
        await this.$store.dispatch('timesheet/approveEvent', {
          eventId: payload.eventId,
          decision: 'rejected',
          adminComment: payload.adminComment || undefined,
        })
        this.$bvToast.toast('Timesheet rejected.', {
          title: 'Success',
          variant: 'warning',
          solid: true,
        })
        this.load()
      } catch (e) {
        this.$bvToast.toast('Failed to reject timesheet.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/layouts/timesheet-shared';

.list-toolbar {
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  padding: 20px;
  margin-bottom: 1rem;
  box-shadow: 0 1px 0 rgba(15, 23, 42, 0.03);
}

.toolbar-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

.list-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.01em;
}

.list-subtitle {
  margin: 4px 0 0;
  font-size: 13px;
  color: #6b7280;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.toolbar-row {
  display: grid;
  grid-template-columns:
    minmax(132px, 0.75fr) minmax(132px, 0.75fr) minmax(180px, 1.1fr)
    minmax(150px, 0.9fr) max-content minmax(180px, 1fr);
  align-items: flex-end;
  gap: 12px;
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.9);

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, minmax(150px, 1fr));
  }

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(150px, 1fr));
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.filter-field-date {
  min-width: 132px;
}

.date-input,
.filter-control {
  width: 100%;
}

/* b-form-datepicker's two-class .b-form-btn-label-control.form-control rule
   beats the app-wide one-class `.form-control` height, so it renders shorter
   than the sibling sm filter controls. Match it back. */
/deep/ .date-input.form-control-sm {
  height: 34px;
  display: flex;
  align-items: center;
}

.week-nav {
  display: flex;
  align-items: center;
  gap: 6px;
  overflow: hidden;
}

.week-nav-btn {
  width: 32px;
  padding-left: 0;
  padding-right: 0;
  flex-shrink: 0;
}

.week-nav-label {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  flex: 1 1 0;
  min-width: 0;
  text-align: center;
  line-height: 1.2;
  /* Never ellipsize the range — if it's ever too tight it wraps at the dash
     (dates use non-breaking spaces internally, see weekLabel). */
  white-space: normal;
  overflow: visible;
}

.filter-label {
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0;
}

/* Events Table — no horizontal scrolling at any width: below 992px the rows
   render stacked (see stacked="lg"), above it the table fits and long values
   wrap within their cells instead of widening the table. */
.table-wrap {
  overflow-x: visible;
}

.action-group {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  gap: 6px;
  justify-content: flex-end;
  white-space: nowrap;
}

/deep/ .events-table {
  @include timesheet-table-headers;
}

/* Phones/tablets: the events table renders stacked (see stacked="lg" in the
   template). Tighten bootstrap-vue's default stacked look into compact
   cards — small uppercase labels in a fixed column, values beside them — so
   entries stay readable all the way down to 320px with no sideways scroll. */
@media (max-width: 991.98px) {
  /* Selector carries all four classes on purpose: bootstrap-vue's own stacked
     rules (.table.b-table.b-table-stacked-lg > tbody > tr > [data-label])
     set a 40% right-aligned label column, and anything weaker loses to them —
     which left the cards hollow, labels floating mid-card. */
  /deep/ .events-table.table.b-table.b-table-stacked-lg {
    > tbody > tr {
      display: block;
      padding: 12px 14px;
      border-bottom: 1px solid #f3f4f6;

      &:last-child {
        border-bottom: 0;
      }
    }

    /* Between phone and the full table, a single column leaves the right
       half of the card empty — flow the fields into two columns instead.
       The worker name and the actions row keep the full width. */
    @media (min-width: 576px) {
      > tbody > tr {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        column-gap: 40px;
        align-items: baseline;
      }

      > tbody > tr > td[data-label='Worker'],
      > tbody > tr > td.actions-cell {
        grid-column: 1 / -1;
      }
    }

    /* Each field: two aligned columns — labels in a fixed left column, all
       values starting at the same x, left-aligned. Labels never wrap; values
       wrap only at word boundaries — no mid-word breaking at any width. */
    > tbody > tr > td {
      display: grid;
      grid-template-columns: 110px minmax(0, 1fr);
      align-items: baseline;
      gap: 12px;
      padding: 3px 0;
      border: 0;
      font-size: 13px;

      &::before {
        content: attr(data-label);
        float: none;
        width: auto;
        margin: 0;
        padding: 0;
        text-align: left;
        white-space: nowrap;
        font-size: 10px;
        font-weight: 600;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: #9ca3af;
      }

      > div {
        width: auto;
        min-width: 0;
        margin: 0;
        padding: 0;
        text-align: left;
        word-break: normal;
        overflow-wrap: normal;
        color: #374151;
      }
    }

    /* Worker name is the card's title: full width, larger, no label. */
    > tbody > tr > td[data-label='Worker'] {
      display: block;
      padding-bottom: 6px;

      &::before {
        display: none;
      }

      > div {
        text-align: left;
        font-size: 15px;
        font-weight: 600;
      }
    }

    /* The actions column has no label — let the buttons/status span the full
       card width. */
    > tbody > tr > td.actions-cell {
      display: block;
      margin-top: 6px;

      &::before {
        display: none;
      }

      > div {
        text-align: left;
      }

      .action-group {
        justify-content: flex-start;
      }
    }
  }
}
</style>
